import {TFunction} from 'i18next';
import {contentAdultMap} from '../../commonSub/hook/useRouteToContentObserver';
import {BADGE_MAP, IS_CLIENT} from '../constant';
import {Page} from '../constant/page';
import {
  IContentMeta,
  INotice,
  VideoSource,
  IExternalVideoType,
  IAdBannerType,
  IAdBannerPage,
  IAdBanner,
  IMomenBannerType,
  IAuthorType,
  IAuthorDetails,
  ITextWithEllipsisItem,
  ITorosInfo,
  BadgeType,
  IWebToAppBanner,
  Badge,
} from '../type';
import {I_ApiMeta} from '../util/api';
import {
  DEFAULT_DATE_FORMAT,
  formatFromServerDate,
  getDuration,
  getIsRecent,
  getTimeAgoText,
  SHORT_DATE_FORMAT,
} from '../util/date';
import {getKeys} from '../util/object';
import {parseLandingUriFromServer} from '../util/PageRouter';
import {getLocalStorageNumber, StorageKeyPrefix} from '../util/storage';
import {convertHexToRgb, toCamelCase} from '../util/string';

const contentPropHandlers: {
  [key in keyof Required<IContentMeta>]: (
    data: I_ContentMeta,
    option?: IContentPropsOption
  ) => IContentMeta[key];
} = {
  id: data => {
    const id = Number(data.id);
    if (data.adult !== undefined) {
      // TODO: 23-3차 스프린트에 아래 주석 부분 추가하자. 현재는 리전 구분없이 18세 이상일때만 팝업 노출한다.
      // contentAdultMap[id] =
      //   data.adult || (REGION === 'idn' && data.ageLimit >= 15);
      contentAdultMap[id] = data.adult;
    }
    return id;
  },
  sid: data => data.seoId,
  // TODO: 제거하자
  rid: data => data.seoId,
  title: data => data.title,
  titleImg: data => getImageUrl(data.titleImageB, {hasAlpha: true}),
  mainImg: data => getImageUrl(data.featuredCharacterImageA, {hasAlpha: true}),
  mainTitleImg: data => getImageUrl(data.titleImageA, {hasAlpha: true}),
  decoImg: data =>
    data.decorationImage
      ? getImageUrl(data.decorationImage, {hasAlpha: true})
      : undefined,
  thumbImg: data => getImageUrl(data.featuredCharacterImageB, {hasAlpha: true}),
  bgImg: (data, option) =>
    getImageUrl(data.backgroundImage, {
      isSmall: !option?.isBigBg,
      hasAlpha: !!option?.isAlphaBg,
    }),
  anchorClip: ({anchorClip}) =>
    anchorClip?.clipUrl
      ? {
          sources: getVideoSources(anchorClip.clipUrl, true),
          // TODO: 현재 jpg 더미가 없어서 데이터 반영되면 jpg 로 변경하자
          motionStartImg: `${getPlatformSource(anchorClip.clipFirstFrame)}.png`,
        }
      : undefined,
  bgColor: data => getColor(data.backgroundColor),
  bgColorRgb: data => convertHexToRgb(data.backgroundColor),
  badges: (data, option) =>
    getSortBadges(
      data.badges
        ?.filter(option?.badgesFilter || (() => true)) // 모든 리전, 모든 배지 노출
        .map(getBadge)
        .filter(item => item !== undefined) || []
    ),
  genre: data => data.genre || '',
  synopsis: data => data.synopsis || '',
  seoKeywords: data => data.seoKeywords || [],
  authors: data =>
    getAuthorDetails(data.authors)
      .filter(
        ({type}) =>
          type === 'author' ||
          type === 'illustrator' ||
          type === 'originalStory'
      )
      .reduce<string[]>((acc, item) => [...acc, ...item.names], [])
      .filter((item, index, arr) => arr.indexOf(item) === index)
      .join(', '),
  authorDetails: data => getAuthorDetails(data.authors),
  motionVideo: ({featuredCharacterAnimation}) =>
    featuredCharacterAnimation
      ? getVideoSources(featuredCharacterAnimation, true)
      : undefined,
  motionStartImg: ({featuredCharacterAnimationFirstFrame}) =>
    featuredCharacterAnimationFirstFrame
      ? `${getPlatformSource(featuredCharacterAnimationFirstFrame)}.png`
      : undefined,
  motionEndImg: ({featuredCharacterAnimationLastFrame}) =>
    featuredCharacterAnimationLastFrame
      ? `${getPlatformSource(featuredCharacterAnimationLastFrame)}.png`
      : undefined,
  promotionVideo: ({ipPromotionVideo}) =>
    ipPromotionVideo
      ? {
          title: ipPromotionVideo.title,
          subTitle: ipPromotionVideo.subtitle,
          verticalSources: getVideoSources(ipPromotionVideo.verticalVideo),
          horizontalSources: getVideoSources(ipPromotionVideo.horizontalVideo),
          thumbImg:
            ipPromotionVideo.thumbnailImage &&
            `${ipPromotionVideo.thumbnailImage}.jpg`,
        }
      : undefined,
  videoClips: data =>
    data.videoClips?.map(item => ({
      key: item.externalVideoKey,
      type: toCamelCase(item.externalVideoFrom) as IExternalVideoType,
    })) || [],
  viewCount: data => data.statistics?.viewCount || 0,
  likeCount: data => data.statistics?.likeCount || 0,
  ageLimit: data => data.ageLimit,
  descLine3: data => data.catchphraseThreeLines,
  descLine2: data => data.catchphraseTwoLines,
  copyright: data => data.copyright,
  isAdult: data => data.adult,
  isStopContent: data => data.status !== 'SELLING',
  shareImg: data =>
    data.sharingThumbnailImage
      ? getPlatformSource(data.sharingThumbnailImage) + '.jpg'
      : '',
};

interface IContentPropsOption {
  isBigBg?: boolean;
  isAlphaBg?: boolean;
  badgesFilter?: (badge: I_Badge) => boolean;
}
export function getContentProps(
  data: Partial<I_ContentMeta>,
  props: Array<keyof IContentMeta>,
  option?: IContentPropsOption
): {[key in keyof IContentMeta]: IContentMeta[key]} {
  return props.reduce((acc, prop) => {
    acc[prop] = contentPropHandlers[prop](data as any, option);
    return acc;
  }, {} as any);
}

export function getBadge({title, value}: I_Badge): Badge {
  let badgeTitle = toCamelCase(title) as BadgeType;

  // 기다무 미사용, 기다무 충전 완료는 같은 뱃지를 사용한다.
  // 기다무 충전 관련 뱃지이며(postfix가 Timer) 기다무 충전 완료(recharged)인 경우에는 Timer를 제거해 노란 뱃지가 나오게 한다.
  // 참고 댓글: https://podotree.atlassian.net/browse/KWQA-16701?focusedCommentId=224847
  if (
    (badgeTitle === 'waitForFreePlusTimer' ||
      badgeTitle === 'waitForFreeTimer') &&
    value?.recharged
  ) {
    badgeTitle = badgeTitle.replace('Timer', '') as BadgeType;
  }

  return {
    ...BADGE_MAP[badgeTitle],
    waitfree: value?.interval
      ? {
          // 현재는 1시간 미만인 경우 0다무로 처리. 분 단위는 나중에 추가 개발 예정.
          hours: getDuration(value.interval).hours,
        }
      : undefined,
  };
}
function getSortBadges(badges: Badge[]) {
  const _badges: Badge[] = [...badges];
  const index1 = _badges.findIndex(
    item => item.imgTextType === 'waitForFreePlusExtended'
  );
  const index2 = _badges.findIndex(
    item => item.imgTextType === 'waitForFreePlus'
  );
  const index3 = _badges.findIndex(item => item.imgType === 'waitForFree');

  index1 !== -1 && _badges.unshift(..._badges.splice(index1, 1));
  index2 !== -1 && _badges.unshift(..._badges.splice(index2, 1));
  index3 !== -1 && _badges.unshift(..._badges.splice(index3, 1));

  return _badges;
}

interface I_UserCashInfo {
  autoPayBenefitRatio?: number;
  autoPayInUse: boolean;
  eventAmount: number;
  expirableAmount?: number;
  firstExpirableAmount?: number;
  firstExpirableDt?: string;
  mileageAmount: number;
  realAmount: number;
  totalAmount: number;
}

export function getUserCashInfo(data: I_UserCashInfo) {
  return {
    totalCash: data.totalAmount,
    purchasedCash: data.realAmount,
    eventCash: data.eventAmount,
    expireInfo:
      data.firstExpirableAmount && data.firstExpirableDt
        ? {
            cash: data.firstExpirableAmount,
            date: formatFromServerDate(
              data.firstExpirableDt,
              SHORT_DATE_FORMAT
            ),
          }
        : undefined,
    autoPay: {
      isOn: data.autoPayInUse,
      benefitRatio: data.autoPayBenefitRatio || 0,
    },
  };
}

export interface I_ContentMeta {
  id: number;
  seoId: string;
  title: string;
  // 큰 이미지
  featuredCharacterAnimation: string;
  featuredCharacterAnimationFirstFrame: string;
  featuredCharacterAnimationLastFrame: string;
  featuredCharacterImageA: string;
  titleImageA: string;
  decorationImage: string;
  // 시간표 이미지
  featuredCharacterImageB: string;
  titleImageB: string;
  lookThroughImages?: string[];
  // 멀티셀에 사용될 영상(얼라이브, 루프)
  anchorClip?: {
    type: string;
    clipUrl: string;
    clipFirstFrame: string;
  };
  backgroundImage: string;
  backgroundColor: string;
  badges?: I_Badge[];
  genre?: string;
  catchphraseThreeLines: string; // 메인 추천탭, 신작/완결탭 3줄 텍스트
  catchphraseTwoLines: string; // 랭킹 1등 작품 2줄 텍스트
  copyright: string;
  synopsis: string; // // 작품홈 > 작품 정보 탭 > "줄거리" 영역
  seoKeywords?: string[];
  authors?: I_Author[];
  ipPromotionVideo?: {
    title: string;
    subtitle?: string;
    verticalVideo: string;
    horizontalVideo: string;
    thumbnailImage?: string;
  };
  videoClips?: Array<{
    externalVideoKey: string;
    externalVideoFrom: I_ExternalVideo;
  }>;
  statistics?: {
    viewCount: number;
    likeCount: number;
  };
  adult: boolean;
  ageLimit: number; // 전체=0, 성인=18,19,20(리전별 상이)
  status: string;
  sharingThumbnailImage: string;
}
export interface I_Badge {
  title: string;
  type: string;
  value?: {
    interval: string;
    recharged?: boolean;
  };
}

export type I_ExternalVideo = 'KAKAO_TV' | 'YOUTUBE' | 'AFREECA_TV';

const WEEK_HOURS = 7 * 24;
export function getHasNew({
  page,
  lastUpdateTime = 0,
}: {
  page: Page;
  lastUpdateTime?: number;
}): boolean {
  if (!IS_CLIENT) {
    return false;
  }
  const lastViewTime =
    getLocalStorageNumber({
      prefix: StorageKeyPrefix.PageViewTime,
      key: page,
    }) || 0;

  return (
    lastViewTime < lastUpdateTime && getIsRecent(lastUpdateTime, WEEK_HOURS)
  );
}

export function getNoticeList(
  t: TFunction,
  noticeList: I_Notice[] = []
): INotice[] {
  return noticeList.map(({id, title, content, createdDateTime}) => ({
    id,
    title,
    contents: content,
    // TODO: 생성기준인지, 업데이트 기준인지 확인
    agoText: getIsRecent(createdDateTime, 24)
      ? getTimeAgoText({oldDate: createdDateTime, now: Date.now()}, t)
      : formatFromServerDate(createdDateTime, DEFAULT_DATE_FORMAT),
  }));
}

interface I_Notice {
  id: number;
  title: string;
  content: string;
  createdDateTime: string;
  updatedDateTime: string;
}

export function getPlatformSource(src?: string) {
  if (src?.endsWith('~os')) {
    return src + '.w';
  } else {
    return src;
  }
}

export function getImageUrl(
  src: string,
  option: {hasAlpha?: boolean; isSmall?: boolean} = {
    hasAlpha: false,
    isSmall: false,
  }
) {
  // TODO: 이미지 정보가 null 혹은 빈 문자열로 내려주는 경우가 있음. 서버쪽 컨벤션 통일되면 if 문 제거하자
  if (!src) return '';
  const res = `${getPlatformSource(src)}.${
    option.hasAlpha ? 'webp.png' : 'webp.jpg'
  }`;
  if (option.isSmall) {
    return res.replace(/\/2x\//, '/1x/');
  } else {
    return res;
  }
}

export function getVideoSources(src: string, hasAlpha = false): VideoSource[] {
  const platformSrc = getPlatformSource(src);
  const webm: VideoSource = {
    type: 'video/webm',
    src: `${platformSrc}.webm`,
  };
  const mov: VideoSource = {
    type: 'video/quicktime',
    src: `${platformSrc}.mov`,
  };
  const mp4: VideoSource = {
    type: 'video/mp4',
    src: `${platformSrc}.mp4`,
  };
  return hasAlpha ? [webm, mov] : [webm, mp4];
}

// TODO:
//   모든 컬러 정보(작품 정보가 아니어도)는 "#" + "{HEX}" 로 내려온다고 함
//   더미 데이터 모두 제거하고 응답 결과도 값이 통일되면 함수는 제거하자 (현재 서버에서 # 없이 내려옴)
export function getColor(color?: string) {
  if (!color) return '';
  return color.includes('#') || color.includes(',') ? color : `#${color}`;
}

const AUTHOR_TYPE_TEXT_KEY_MAP: {[key in IAuthorType]: string} = {
  author: 'contenthome_info_tab_author',
  illustrator: 'contenthome_info_tab_illustrator',
  originalStory: 'contenthome_info_tab_original',
  adaptation: 'contenthome_info_tab_adaptation',
  color: 'contenthome_info_tab_color',
  publisher: 'contenthome_info_tab_publisher',
};

export function getIsEnd(meta?: I_ApiMeta) {
  return !!meta?.pagination?.last;
}
export function getTotalCount(meta?: I_ApiMeta) {
  return meta?.pagination?.totalCount ?? 0;
}

export function getAdBanner({
  page,
  data,
  needDefaultType = false,
}: {
  page: IAdBannerPage;
  data?: I_AdBanner;
  needDefaultType?: boolean;
}): IAdBanner | undefined {
  if (!data) return undefined;

  const {id, module: _type, attribute, exceptionComponent, placement} = data;
  const isMoment = data.moment;
  const type = toCamelCase(_type) as IAdBannerType;
  const adminAd = attribute?.components;
  const defaultBanner =
    exceptionComponent && getAdminAdComponent(exceptionComponent);

  // isMoment로 분기처리 하지 않는 이유는 운영배너 우선순위가 더 높기 때문
  return adminAd
    ? {
        id,
        isMoment: false,
        type,
        adminAd: getAdminAdComponent(adminAd),
        defaultBanner,
        placement,
        cardgroupId: attribute?.operationId,
      }
    : isMoment
      ? {
          id,
          isMoment,
          type: type as IMomenBannerType,
          page,
          defaultBanner,
          placement,
          cardgroupId: exceptionComponent?.operationId,
        }
      : needDefaultType && defaultBanner
        ? {
            id,
            isMoment: false,
            type: 'default',
            defaultBanner,
            placement,
            cardgroupId: exceptionComponent?.operationId,
          }
        : undefined;
}

function getAdminAdComponent(adminAd: I_AdBannerComponent) {
  return {
    thumbImg: getImageUrl(adminAd.thumbnailImage, {hasAlpha: true}),
    landingUri: parseLandingUriFromServer(adminAd.url),
    bgColor: adminAd.backgroundColor,
    textColor: adminAd.titleColor,
    title: adminAd.title,
    subTitle: adminAd.subtitle,
    decorationImg:
      adminAd.decorationImage &&
      getImageUrl(adminAd.decorationImage, {hasAlpha: true}),
  };
}

export interface I_AdBanner {
  id: string;
  moment: boolean;
  module: string;
  attribute?: {
    id: string;
    key: string;
    components?: I_AdBannerComponent;
    operationId?: number;
  };
  exceptionComponent?: I_AdBannerComponent;
  placement: string;
}

interface I_AdBannerComponent {
  backgroundColor: string;
  titleColor: string;
  subtitle?: string;
  title: string;
  thumbnailImage: string;
  url: string;
  decorationImage?: string;
  operationId?: number;
}

interface I_Author {
  id: number;
  name: string;
  type: string;
  order: number;
}

function getAuthorDetails(authors: I_Author[] = []) {
  return authors.reduce<IAuthorDetails>((acc, item) => {
    const type = toCamelCase(item.type) as IAuthorType;
    const storedAuthor = acc.find(item => item.type === type);
    if (storedAuthor) {
      storedAuthor.names.push(item.name);
    } else {
      acc.push({
        type,
        typeKey: AUTHOR_TYPE_TEXT_KEY_MAP[type],
        names: [item.name],
      });
    }
    return acc;
  }, []);
}

/**
 * 앞뒤 또는 가운데 하나의 변수만 붙을 수 있다
 * ex) invalid case
 * `{aa}bb{cc}`
 * ex) valid case
 * `aa{bb}cc`
 * `aa{bb}`
 * `{aa}bb`
 * `aa`
 * `{aa}`
 */
export function getTextWithEllipsis(
  text: string,
  keyMap?: {[key in string]: string}
): ITextWithEllipsisItem[] {
  const valueKeys = keyMap ? getKeys(keyMap) : undefined;
  const key = valueKeys?.find(item => text.includes(`{${item}}`));
  if (key && keyMap) {
    const comp = `{${key}}`;
    if (text === comp) {
      return [{isEllipsis: true, text: keyMap[key]}];
    } else if (text.startsWith(comp)) {
      return [
        {isEllipsis: true, text: keyMap[key]},
        {isEllipsis: false, text: text.replace(comp, '')},
      ];
    } else if (text.endsWith(comp)) {
      return [
        {isEllipsis: false, text: text.replace(comp, '')},
        {isEllipsis: true, text: keyMap[key]},
      ];
    } else {
      const texts = text.split(comp);
      return [
        {isEllipsis: false, text: texts[0]},
        {isEllipsis: true, text: keyMap[key]},
        {isEllipsis: false, text: texts[1]},
      ];
    }
  } else {
    return [{isEllipsis: true, text}];
  }
}

export interface I_TorosLog {
  hashKey: string;
  impressionId: string;
}
export function getTorosInfo(torosLog: I_TorosLog): ITorosInfo {
  return {
    torosImpId: torosLog.impressionId,
    torosFileHashKey: torosLog.hashKey,
  };
}

interface I_WebToAppBanner {
  image: string;
  url: string;
}
export function getWebToAppBanner(
  data: I_WebToAppBanner
): IWebToAppBanner | undefined {
  // 배너 미노출은 image && url 둘다 내려주지 않는다고 협의
  return data?.image && data?.url
    ? {
        image: getImageUrl(data.image, {hasAlpha: true}),
        landingUri: parseLandingUriFromServer(data.url),
      }
    : undefined;
}
